import type { Policy, PolicyVendorContext, VendorRunout } from "@/types";

// =============================================================================
// Policy vendor selector logic (#5591)
//
// Pure helpers that turn a policy's TPA/Rx vendor context into the pinned-group
// ordering, per-vendor role tags, the pre-fill default, and the run-out cutover
// notice consumed by VendorAssignmentDialog. Kept framework-free so the
// defaulting rules (which are the risky part) are unit-testable without rendering.
//
// Part B date logic is deliberately NOT implemented here: at selector time we
// have not parsed the file, so we cannot compare a claim's PAID date against a
// vendor's runout_cutover_date. The default is the policy's single primary TPA;
// run-out is surfaced only as a non-blocking warning. When date-based switching
// is added later, the rule is: compare the claim's paid date to the cutover.
// =============================================================================

/** Per-vendor role metadata for a vendor attached to the policy. */
export interface VendorRoleMeta {
  /** Vendor administers Rx/PBM claims on this policy. */
  isRx: boolean;
  /** Vendor administers in a run-out capacity (#5325); absence in the run-out array = primary. */
  isRunout: boolean;
  /** ISO "YYYY-MM-DD" cutover date, when the vendor is run-out and one is set. */
  cutoverDate?: string;
}

const runoutMap = (entries?: VendorRunout[]): Map<string, VendorRunout> =>
  new Map((entries ?? []).map((e) => [e.vendor_id, e]));

/**
 * Merge a role's run-out state across policies at UNION grain. A vendor is run-out
 * only when it is flagged run-out on EVERY policy that lists it in this role. If it
 * is a current primary on any listing policy it stays a primary candidate, so a
 * client with two policies whose primaries differ still hits the "2+ primaries →
 * don't guess" rule (#5591 iter-2). An OR here would drop a vendor that is primary
 * on one policy and run-out on another, turning that ambiguity into a confident
 * wrong prefill. The cutover date comes only from run-out entries, so a primary
 * entry that happens to carry a date (backend ships those) never leaks onto a
 * run-out result.
 */
const mergeRoleRunout = (
  policies: Policy[],
  vendorIds: string[],
  listOf: (p: Policy) => string[] | undefined,
  runoutOf: (p: Policy) => VendorRunout[] | undefined
): VendorRunout[] => {
  const out: VendorRunout[] = [];
  for (const id of vendorIds) {
    let listCount = 0;
    let runoutCount = 0;
    let earliest: string | undefined;
    for (const p of policies) {
      if (!(listOf(p) ?? []).includes(id)) continue;
      listCount++;
      const entry = (runoutOf(p) ?? []).find((e) => e.vendor_id === id);
      if (entry?.is_runout) {
        runoutCount++;
        if (entry.runout_cutover_date && (!earliest || entry.runout_cutover_date < earliest)) {
          earliest = entry.runout_cutover_date;
        }
      }
    }
    // Run-out only if run-out everywhere it's listed; primary-anywhere is omitted
    // (absent = primary, per the backend junction contract).
    if (listCount > 0 && runoutCount === listCount) {
      out.push({ vendor_id: id, is_runout: true, runout_cutover_date: earliest });
    }
  }
  return out;
};

/**
 * Build one vendor context from a set of policies (#5591). For a single-policy set
 * this is that policy's vendors; for a client-scoped upload spanning multiple
 * policies it is the UNION, so pinning surfaces every administrator and the
 * single-primary default rule (resolvePrimaryTpaDefault) evaluates at the client
 * grain — with two policies' distinct primaries it sees 2+ primaries and won't
 * guess, instead of confidently prefilling one policy's TPA.
 */
export const buildPolicyVendorContext = (policies: Policy[]): PolicyVendorContext => {
  const tpaVendorIds: string[] = [];
  const rxVendorIds: string[] = [];
  const seenTpa = new Set<string>();
  const seenRx = new Set<string>();

  for (const p of policies) {
    for (const id of p.tpa_vendor_ids ?? []) {
      if (!seenTpa.has(id)) {
        seenTpa.add(id);
        tpaVendorIds.push(id);
      }
    }
    for (const id of p.rx_vendor_ids ?? []) {
      if (!seenRx.has(id)) {
        seenRx.add(id);
        rxVendorIds.push(id);
      }
    }
  }

  return {
    tpaVendorIds,
    rxVendorIds,
    tpaRunout: mergeRoleRunout(
      policies,
      tpaVendorIds,
      (p) => p.tpa_vendor_ids,
      (p) => p.tpa_vendor_runout
    ),
    rxRunout: mergeRoleRunout(
      policies,
      rxVendorIds,
      (p) => p.rx_vendor_ids,
      (p) => p.rx_vendor_runout
    ),
  };
};

/**
 * Role metadata for every vendor on the policy, keyed by vendor id. The displayed
 * run-out role reflects the vendor's TPA membership when it is a TPA (the claims
 * default is TPA-derived, so the tag must agree with the prefill), and its Rx
 * membership only for Rx-only vendors. Roles are NOT OR'd across TPA and Rx — a
 * primary-TPA / run-out-Rx vendor reads as its TPA role, matching what gets stamped.
 */
export const buildVendorRoleMeta = (ctx: PolicyVendorContext): Map<string, VendorRoleMeta> => {
  const tpaR = runoutMap(ctx.tpaRunout);
  const rxR = runoutMap(ctx.rxRunout);
  const tpaSet = new Set(ctx.tpaVendorIds);
  const rxSet = new Set(ctx.rxVendorIds);
  const meta = new Map<string, VendorRoleMeta>();

  for (const id of [...ctx.tpaVendorIds, ...ctx.rxVendorIds]) {
    if (meta.has(id)) continue;
    const isTpa = tpaSet.has(id);
    // TPA role wins for vendors that administer medical claims; Rx-only vendors
    // fall back to their Rx role. One role per vendor, never a mix.
    const role = isTpa ? tpaR.get(id) : rxR.get(id);
    meta.set(id, {
      isRx: rxSet.has(id),
      isRunout: role?.is_runout ?? false,
      cutoverDate: role?.runout_cutover_date,
    });
  }
  return meta;
};

/** Policy vendor ids in pin order (TPA first, then Rx), de-duplicated. */
export const orderedPolicyVendorIds = (ctx: PolicyVendorContext): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of [...ctx.tpaVendorIds, ...ctx.rxVendorIds]) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
};

/**
 * The single primary TPA to pre-fill, or undefined when the choice is ambiguous.
 * Primary = a TPA vendor not flagged run-out. Returns an id only when exactly one
 * primary TPA exists — with 0 or 2+ primaries we pin but never guess (#5591). Rx
 * vendors are intentionally excluded: a claims file defaults to its medical
 * administrator, and the user picks Rx explicitly when the file is a PBM extract.
 */
export const resolvePrimaryTpaDefault = (ctx: PolicyVendorContext): string | undefined => {
  const runout = runoutMap(ctx.tpaRunout);
  const primaries = ctx.tpaVendorIds.filter((id) => !(runout.get(id)?.is_runout ?? false));
  return primaries.length === 1 ? primaries[0] : undefined;
};

/** A dated run-out administrator, used to build the non-blocking cutover warning. */
export interface RunoutNotice {
  vendorId: string;
  /** ISO "YYYY-MM-DD". */
  cutoverDate: string;
}

/**
 * The earliest dated run-out cutover on the policy, or null when none exists.
 * Only run-out vendors carrying a cutover date qualify — an undated run-out flag
 * gives nothing concrete to warn about.
 */
export const resolveRunoutNotice = (ctx: PolicyVendorContext): RunoutNotice | null => {
  const dated: RunoutNotice[] = [];
  const collect = (entries?: VendorRunout[]) => {
    for (const e of entries ?? []) {
      if (e.is_runout && e.runout_cutover_date) {
        dated.push({ vendorId: e.vendor_id, cutoverDate: e.runout_cutover_date });
      }
    }
  };
  collect(ctx.tpaRunout);
  collect(ctx.rxRunout);
  if (dated.length === 0) return null;
  return dated.sort((a, b) => a.cutoverDate.localeCompare(b.cutoverDate))[0];
};

/**
 * Format an ISO "YYYY-MM-DD" date as M/D/YYYY (or M/D/YY with shortYear) without
 * constructing a Date — avoids the UTC-midnight-parses-as-previous-day drift.
 */
export const formatCutover = (iso: string, opts?: { shortYear?: boolean }): string => {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${Number(m)}/${Number(d)}/${opts?.shortYear ? y.slice(2) : y}`;
};

/** Short role tag shown next to a pinned vendor in the selector. */
export const vendorRoleTag = (meta: VendorRoleMeta): { label: string; isRunout: boolean } => {
  if (meta.isRunout) {
    const suffix = meta.cutoverDate ? ` · ${formatCutover(meta.cutoverDate, { shortYear: true })}` : "";
    return { label: `Run-out${suffix}`, isRunout: true };
  }
  return { label: meta.isRx ? "Primary · Rx" : "Primary", isRunout: false };
};

// =============================================================================
// Administrator radio choices (#7552)
//
// The prepare step offers the policy's administrators as radios, each with a
// role label and one line of helper text that carries the rule the amber banner
// and the "from policy" chip used to carry. Pure so the wording is unit-tested
// without rendering.
// =============================================================================

/** ISO "YYYY-MM-DD" minus one day, computed in UTC so no timezone drift. */
export const isoMinusOneDay = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const t = new Date(Date.UTC(y, m - 1, d) - 24 * 60 * 60 * 1000);
  const mm = String(t.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(t.getUTCDate()).padStart(2, "0");
  return `${t.getUTCFullYear()}-${mm}-${dd}`;
};

/** Role label for a radio: "Primary TPA" · "Rx" · "Run-out from 1/1/2026" · "Run-out". */
export const administratorRoleLabel = (meta: VendorRoleMeta): string => {
  if (meta.isRunout) return meta.cutoverDate ? `Run-out from ${formatCutover(meta.cutoverDate)}` : "Run-out";
  return meta.isRx ? "Rx" : "Primary TPA";
};

/**
 * One line under a radio stating the rule for that administrator.
 *
 * @param meta - the vendor's role on the policy
 * @param runoutCutover - the policy's earliest dated run-out cutover (ISO), if any;
 *   lets the primary's line say through when its claims run.
 */
export const administratorChoiceHelperText = (meta: VendorRoleMeta, runoutCutover?: string | null): string => {
  if (meta.isRunout) {
    return meta.cutoverDate
      ? `Pick this for claims paid on or after ${formatCutover(meta.cutoverDate)}.`
      : "Run-out administrator.";
  }
  if (meta.isRx) return "Pharmacy claims.";
  return runoutCutover
    ? `From the policy. Claims paid through ${formatCutover(isoMinusOneDay(runoutCutover))}.`
    : "From the policy.";
};
